import { useEffect, useState } from 'react';
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';

import { PageUrlKey } from '@/entities/page-url-key';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/siteview/components/ui/card';

import { useAvailablePages } from '../hooks/use-available-pages';
import { UpdateDisplayPreferences } from '../mutations';
import { GetDisplayPreferences } from '../queries';

export function DisplayPreferencesSection() {
  const [showDisplaySettingsSuccess, setShowDisplaySettingsSuccess] =
    useState(false);
  const [selectedLandingPage, setSelectedLandingPage] = useState<string>('');

  const { default: defaultPages, main: mainPages } = useAvailablePages();

  const { data: displayPreferences } = GetDisplayPreferences.useQuery();
  const updateDisplayPreferencesMutation =
    UpdateDisplayPreferences.useMutation();

  useEffect(() => {
    if (displayPreferences?.defaultLandingPage && !selectedLandingPage) {
      setSelectedLandingPage(displayPreferences.defaultLandingPage);
    }
  }, [displayPreferences?.defaultLandingPage, selectedLandingPage]);

  const displaySettingsButtonText = updateDisplayPreferencesMutation.isLoading
    ? 'Saving...'
    : 'Save Display Preferences';

  const hasChanges =
    selectedLandingPage !== displayPreferences?.defaultLandingPage;
  const isDisabled = updateDisplayPreferencesMutation.isLoading || !hasChanges;

  const handleDisplaySettingsClick = async () => {
    try {
      await updateDisplayPreferencesMutation.mutateAsync({
        defaultLandingPage: selectedLandingPage as PageUrlKey,
      });
      setShowDisplaySettingsSuccess(true);
      setTimeout(() => {
        setShowDisplaySettingsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to update display preferences:', error);
    }
  };

  const defaultValue =
    selectedLandingPage ||
    displayPreferences?.defaultLandingPage ||
    defaultPages[0]?.value ||
    mainPages[0]?.value ||
    'home';

  return (
    <Card data-testid="display-preferences-section">
      <CardHeader>
        <CardTitle>Display Preferences</CardTitle>
        <CardDescription>
          Customize how information is displayed in your account
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="defaultLanding">Default Landing Page</Label>
          <Select
            value={selectedLandingPage || defaultValue}
            onValueChange={setSelectedLandingPage}
          >
            <SelectTrigger
              className="w-full min-w-[280px] md:w-1/2"
              data-testid="default-landing-page-select-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              className="min-w-[280px]"
              data-testid="default-landing-page-select-content"
            >
              {defaultPages.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Default</SelectLabel>
                  {defaultPages.map((page) => (
                    <SelectItem
                      key={page.value}
                      value={page.value}
                      data-testid="default-landing-page-select-item-default"
                    >
                      {page.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {mainPages.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Main Pages</SelectLabel>
                  {mainPages.map((page) => (
                    <SelectItem
                      key={page.value}
                      value={page.value}
                      data-testid="default-landing-page-select-item-main"
                    >
                      {page.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          <Button
            data-testid="display-settings-save-button"
            onClick={handleDisplaySettingsClick}
            disabled={isDisabled}
          >
            {displaySettingsButtonText}
          </Button>
          {showDisplaySettingsSuccess && (
            <div
              data-testid="display-settings-save-success-message"
              className="text-sm text-primary-500"
            >
              Settings saved!
            </div>
          )}
        </div>
        {updateDisplayPreferencesMutation.error != null && (
          <div
            data-testid="display-preferences-error-message"
            className="mt-2 text-sm text-red-500"
          >
            Failed to save display preferences. Please try again.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
